import { useQuery } from "@tanstack/react-query";
import { statSeason, darkTeams, noShadowTeams } from "./vars";
import teamsBasic from "../data/teamsBasic2025.json";
import { GetTeamRecord } from "./getTeamData";

const rapidApiHost = process.env.REACT_APP_RAPID_API_HOST;
const rapidApiKey = process.env.REACT_APP_RAPID_API_KEY;

 export function useTeamSched(team) {
  return useQuery({
    queryKey: ["teamSched" + team],
    queryFn: async () => {
      try {
        // Try backend cache first
        const backendUrl = `/api/nfl-schedule/${team}/${statSeason}`;
        const response = await fetch(backendUrl);
        
        if (response.ok) {
          const data = await response.json();
          return {
            ...data,
            fetchedAt: new Date().toLocaleTimeString(),
          };
        }
        
        // Fallback to direct RapidAPI if backend fails
        console.warn('Backend unavailable, falling back to RapidAPI');
        throw new Error('Backend unavailable');
      } catch (backendError) {
        // Direct RapidAPI fallback
        const response = await fetch(
          "https://tank01-nfl-live-in-game-real-time-statistics-nfl.p.rapidapi.com/getNFLTeamSchedule?teamAbv=" +
            team +
            "&season=" +
            statSeason,
          {
            method: "GET",
            headers: {
              "x-rapidapi-host": rapidApiHost,
              "x-rapidapi-key": rapidApiKey,
            },
          }
        );
        const data = await response.json();
        return {
          ...data,
          fetchedAt: new Date().toLocaleTimeString(),
        };
      }
    },
    staleTime: 8 * 60 * 60 * 1000, // 8 hours
    cacheTime: 168 * 60 * 60 * 1000, // 7 days
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}

  export function GetTeamSched(props) {
  //props: t=team(DET) ps=true/false show preseason

  const { data, isLoading, error } = useTeamSched(props.t);

  if (isLoading) return <div>Loading...</div>;
  if (error) return <div>Error: {error.message}</div>;

  //console.log(props)
  const showPreseason = props.ps;

  // Extract the schedule array from the nested structure
  const preseason =
    data?.body?.schedule.filter((p) => p.seasonType === "Preseason") || [];
  const schedule =
    data?.body?.schedule.filter((p) => p.seasonType !== "Preseason") || [];
  const team = data?.body?.team || [];
  const bye = props.bye;
  const preByeSched = schedule?.slice(0, bye - 1);
  const postByeSched = schedule?.slice(bye - 1);
  const byeArr = [
    {
      away: null,
      gameDate: "bye",
      gameID: "bye",
      gameStatus: "bye",
      gameStatusCode: "0",
      gameTime: null,
      gameTime_epoch: null,
      gameWeek: "Week " + bye,
      home: null,
      seasonType: "Regular Season",
      teamIDAway: null,
      teamIDHome: null,
    },
  ];
  const newSchedule = preByeSched?.concat(byeArr).concat(postByeSched);
  //console.log(newSchedule);

  // Filter out duplicate gameWeeks, preferring the one without TBD gameTime
  const uniqueSchedule = newSchedule?.reduce((acc, game) => {
    const existingIndex = acc.findIndex((g) => g.gameWeek === game.gameWeek);
    if (existingIndex === -1) {
      // gameWeek doesn't exist yet, add it
      return [...acc, game];
    } else {
      // gameWeek exists, replace if current game has better data (non-TBD)
      if (acc[existingIndex].gameTime === "TBD" && game.gameTime !== "TBD") {
        acc[existingIndex] = game;
      }
      return acc;
    }
  }, []);

  return (
    <>
      {showPreseason
        ? preseason?.map((game, i) => (
            <GameCard key={i} game={game} team={team} preseason={true} />
          ))
        : ""}
      {uniqueSchedule?.map((game, i) => (
        <GameCard key={i} game={game} team={team} preseason={false} />
      ))}
    </>
  );
}

// Separate component for each game
function GameCard({ game, team, preseason }) {
  
  const teamData = teamsBasic?.find((t) => t.Key === team);
  const darkTeam = darkTeams?.includes(teamData?.Key);
  const isHomeGame = game.home === team;
  const opponent = isHomeGame ? game.away : game.home;
  const gameResult = getGameResult(game, isHomeGame);
  const isPreseason = preseason;

  if (game.gameID === "bye") {
    return (
      <>
        <div
          className="schedGridRow"
          style={{
            color:
              "#" +
              (!darkTeam ? teamData?.SecondaryColor : teamData?.TertiaryColor),
          }}
        >
          <div className="gameWeek">
            {game.gameWeek
              .replace("Week ", "")
              .replace("Preseason ", "P")
              .replace("Hall of Fame Weekend", "HoF")}
          </div>
          <div></div>
          <div className="byeCard">BYE</div>
          <div></div>
          <div></div>
        </div>
      </>
    );
  }

  return (
    <>
      <div
        className={isPreseason ? "schedGridRow preseasonRow" : "schedGridRow"}
        style={{
          color:
            "#" +
            (!darkTeam ? teamData.SecondaryColor : teamData.TertiaryColor),
        }}
      >
        <div className="gameWeek">
          {game.gameWeek
            .replace("Week ", "")
            .replace("Preseason ", "P")
            .replace("Hall of Fame Weekend", "HoF")
            .replace("Wild Card", "WC")
            .replace("Divisional Round", "Div")
            .replace("Conference Championship", "Conf")
            .replace("Super Bowl", "SB")}
        </div>
        <div className="gameDate">{formatGameDate(game.gameDate)}</div>
        <div className="gameOpponent">{getTeamInfo(opponent, isHomeGame)}</div>
        <div className="gameScore">
          {game.gameStatus === "Completed" && (
            <div>
              <div>
                {isHomeGame
                  ? `${game.homePts}-${game.awayPts}`
                  : `${game.awayPts}-${game.homePts}`}
              </div>
            </div>
          )}
          {game.gameStatus === "Scheduled" && <div></div>}
        </div>
        <div className={"gameResult " + gameResult}>{gameResult}</div>
      </div>
    </>
  );
}
// Helper functions

export function getTeamInfo(opponent, isHomeGame) {
  const teamData = teamsBasic.find((t) => t.Key === opponent);
  const darkTeam = darkTeams.includes(teamData.Key);

  return (
    <>
      <div
        className="teamCard"
        style={{
          //background: "#" + teamData.PrimaryColor,
          background:
            "linear-gradient(15deg,#000 -100%, #" +
            teamData.PrimaryColor +
            " 40%)",
          color:
            "#" +
            (!darkTeam ? teamData.SecondaryColor : teamData.TertiaryColor),
          textShadow: noShadowTeams.includes(teamData.Key)
            ? ".5px .5px 0px #ffffff36"
            : "1px 1px 0 #00000085",
        }}
      >
        <div
          className={"opponentLogo " + teamData.Key}
          style={{
            backgroundImage: "url(" + teamData.WikipediaLogoURL + ")",
          }}
        ></div>
        <span className="homeAway">{isHomeGame ? "vs " : "@ "}</span>
        <span className="opponent">
          {teamData.Name} {darkTeam} <GetTeamRecord t={teamData.Key} />
        </span>
      </div>
    </>
  );
}
function getGameResult(game, isHomeGame) {
  if (game.gameStatus !== "Completed") return null;

  if (isHomeGame) {
    return game.homeResult;
  } else {
    return game.awayResult;
  }
}

function formatGameDate(dateString) {
  // Convert "20250731" to readable format
  const year = dateString.substring(0, 4);
  const month = dateString.substring(4, 6);
  const day = dateString.substring(6, 8);

  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}
